import i18n from 'i18next';
import LanguageDetector, { CustomDetector, DetectorOptions } from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export const defaultLanguage = 'en';
const supportedLanguages = ['en', 'de', 'fr'];

const getBrowserLanguage = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  // Get browser language
  const browserLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage;
  const langCode = browserLang?.split('-')[0];

  // Return if it's a supported language
  if (langCode && supportedLanguages.includes(langCode)) {
    return langCode;
  }

  return undefined;
};

const myDetector: CustomDetector = {
  name: 'myDetectorsName',

  lookup(options: DetectorOptions) {
    // 1. Check localStorage first (user preference)
    const storedLang = options.lookupLocalStorage ? localStorage.getItem(options.lookupLocalStorage) : undefined;
    if (storedLang && supportedLanguages.includes(storedLang)) {
      return storedLang;
    }

    // 2. Check browser language
    const browserLang = getBrowserLanguage();
    if (browserLang) {
      return browserLang;
    }

    // 3. Fallback to default
    return defaultLanguage;
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(myDetector);

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(languageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    detection: {
      order: ['myDetectorsName'],
      lookupLocalStorage: 'i18nextLng',
    },
    debug: false,
    fallbackLng: defaultLanguage,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    resources: {
      en: {
        translation: {
          header: {
            title: `${import.meta.env.VITE_SITE_NAME}`,
            privacy: `Please note that entering confidential, personal, or internal information into this field is not permitted. You are responsible for how you use the generated content.
`,
            slogan: 'Helps you write clear, inclusive, and on-brand text.',
          },
          from: 'from',
          question: {
            label: 'Your question',
          },
          answers: {
            oneMoment: 'One moment please...',
            answer: 'New and improved text suggestion',
          },
          footer: {
            credits: 'Powered by <1>Liip</1>',
            disclaimer: 'AI-generated suggestions. Please review carefully before use.',
            selectLanguage: 'Select language',
            selectModel: 'Select AI Model',
          },
          textgen: {
            note: "Note: Liip TextMate is optimized for Liip's content guidelines and tone of voice. We optimize for language levels B1 to A2.",
            sell: "Interested in a custom version for your organization? <a href='mailto:contact@liip.ch' class='button button--small'>Get in touch with us</a>",
            inputLanguage: 'Language',
            shortenCta: 'Shorten text',
            shortenLabel: 'Shorten text to the following length (approximate word count):',
            wordCountLabel: 'Word count',
            textLengthLabel: 'Text length',
            copyButton: 'Copy',
            copiedButton: 'Copied',
            wordsMax: 'Max.',
            wordsUnit: 'words',
            placeholderExample: 'e.g. 120',
            placeholder: 'Enter your text',
            reset: 'Reset',
            reviseText: 'Revise text',
          },
        },
      },
      de: {
        translation: {
          header: {
            title: `${import.meta.env.VITE_SITE_NAME}`,
            privacy: `Bitte beachten Sie, dass das Eingeben von vertraulichen, persönlichen oder internen Informationen in dieses Feld nicht gestattet ist. Sie sind verantwortlich für die Verwendung der generierten Inhalte.
`,
            slogan: 'Hilft Ihnen, klare, inklusive und markenkonforme Texte zu schreiben.',
          },
          from: 'vom',
          question: {
            label: 'Deine Frage',
          },
          answers: {
            oneMoment: 'Einen Moment bitte...',
            answer: 'Neuer und verbesserter Textvorschlag',
          },
          footer: {
            credits: 'Entwickelt von <1>Liip</1>',
            disclaimer: 'KI-generierte Vorschläge. Bitte vor der Verwendung sorgfältig überprüfen.',
            selectLanguage: 'Sprache auswählen',
            selectModel: 'KI-Modell auswählen',
          },
          textgen: {
            note: 'Hinweis: Der Liip TextMate ist auf die Content Guidelines und Tonalität von Liip optimiert. Wir optimieren auf das Sprachniveau B1 bis A2.',
            sell: "Interessiert an einer individuellen Version für Ihre Organisation? <a href='mailto:contact@liip.ch' class='button button--small'>Kontaktieren Sie uns</a>",
            inputLanguage: 'Sprache',
            shortenCta: 'Text kürzen',
            shortenLabel: 'Text kürzen auf folgende Länge (ungefähre Anzahl Wörter):',
            wordCountLabel: 'Anzahl Wörter',
            textLengthLabel: 'Textlänge',
            copyButton: 'Kopieren',
            copiedButton: 'Kopiert',
            wordsMax: 'Max.',
            wordsUnit: 'Wörter',
            placeholderExample: 'z. B. 120',
            placeholder: 'Geben Sie Ihren Text ein',
            reset: 'Zurücksetzen',
            reviseText: 'Text überarbeiten',
          },
        },
      },
      fr: {
        translation: {
          header: {
            title: `${import.meta.env.VITE_SITE_NAME}`,
            privacy: `Veuillez noter que l'entrée d'informations confidentielles, personnelles ou internes dans ce champ n'est pas autorisée. Vous êtes responsable de l'utilisation du contenu généré.
`,
            slogan: 'Vous aide à rédiger des textes clairs, inclusifs et conformes à la marque.',
          },
          from: 'du',
          question: {
            label: 'Votre question',
          },
          answers: {
            oneMoment: "Un instant s'il vous plaît...",
            answer: 'Nouvelle suggestion de texte améliorée',
          },
          footer: {
            credits: 'Développé par <1>Liip</1>',
            disclaimer: 'Suggestions générées par IA. Veuillez vérifier attentivement avant utilisation.',
            selectLanguage: 'Sélectionner la langue',
            selectModel: 'Sélectionner le modèle IA',
          },
          textgen: {
            note: "Remarque : Liip TextMate est optimisé pour respecter les directives de contenu et le ton de Liip. Nous l'optimisons pour les niveaux de langue B1 à A2.",
            sell: "Intéressé par une version personnalisée pour votre organisation ? <a href='mailto:contact@liip.ch' class='button button--small'>Contactez-nous</a>",
            inputLanguage: 'Langue',
            shortenCta: 'Raccourcir',
            shortenLabel: 'Raccourcir le texte à la longueur suivante (nombre de mots approximatif) :',
            wordCountLabel: 'Nombre de mots',
            textLengthLabel: 'Longueur du texte',
            copyButton: 'Copier',
            copiedButton: 'Copié',
            wordsMax: 'Max.',
            wordsUnit: 'mots',
            placeholderExample: 'p. ex. 120',
            placeholder: 'Entrez votre texte',
            reset: 'Réinitialiser',
            reviseText: 'Réviser le texte',
          },
        },
      },
    },
  });

export default i18n;
